/** True for values that look like a legal first+last name, e.g. "Natasha Goss". */
export function looksLikeLegalName(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed || !/\s/.test(trimmed)) return false;
  return /^[A-Za-z][A-Za-z.'-]*(?:\s+[A-Za-z][A-Za-z.'-]*)+$/.test(trimmed);
}

export function isPlaceholderUsername(value: string): boolean {
  return /^athlete-\d+$/i.test(value.trim());
}

export function systemDefaultUsername(athleteId: number): string {
  return `athlete-${athleteId}`;
}

/** Admin-set ledger names that happen to look like first+last. Auto-write must not hold these. */
const ESTABLISHED_SPACED_USERNAMES = new Set([
  'willy wingo',
  'el apagon',
  'el apagón',
  'ernest j.',
  'sam spade',
]);

/**
 * Strava first/last (or handle-shaped legal name) that must never appear on the live site.
 * Established WINGO nicknames with spaces are allowed.
 */
export function isStravaIdentityLeak(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (!looksLikeLegalName(trimmed)) return false;
  return !ESTABLISHED_SPACED_USERNAMES.has(trimmed.toLowerCase());
}

/** Safe to show on wingo320.com, public APIs, and the Google Sheet log. */
export function isPublicSafeUsername(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  return !isStravaIdentityLeak(trimmed);
}

/** Admin-chosen WINGO handle. Rejects Strava/legal names. */
export function isAllowedAdminUsername(value: string): boolean {
  return isPublicSafeUsername(value);
}

export const STRAVA_NAME_BLOCKED_MESSAGE =
  'Use a WINGO username, not a Strava or legal name. Unnamed athletes stay on the athlete-{id} default until you set one.';

/** Usernames that must not be written to the public ledger. */
export function shouldHoldLedgerWrite(username: string): boolean {
  const trimmed = username.trim();
  if (!trimmed) return true;
  return isStravaIdentityLeak(trimmed);
}

export function assertPublicSafeLedgerUsername(username: string): void {
  if (shouldHoldLedgerWrite(username)) {
    throw new Error(
      `Refusing to write Strava/legal name to the public ledger: "${username.trim()}". ${STRAVA_NAME_BLOCKED_MESSAGE}`,
    );
  }
}

/**
 * WINGO ledger name for a Strava signup.
 * Never uses first/last name or Strava handle. Never overwrites an admin mapping.
 * New or leaked names become athlete-{id} until an admin sets the username.
 */
export function resolveSignupUsername(options: {
  existingUsername?: string | null;
  athleteId: number;
}): string {
  const existing = options.existingUsername?.trim() ?? '';
  if (existing && isPublicSafeUsername(existing)) {
    return existing;
  }

  return systemDefaultUsername(options.athleteId);
}

/**
 * Clipboard / admin display name. Never a Strava or legal name.
 * Unknown athletes stay on athlete-{id} until an admin sets a WINGO handle.
 */
export function clipboardSafeUsername(
  username: string | null | undefined,
  athleteId: number,
): string {
  return resolveSignupUsername({ existingUsername: username, athleteId });
}

/** Strip a leaked name when no athlete id is available (e.g. sheet duplicate metadata). */
export function redactLeakedUsername(username: string | null | undefined): string {
  const trimmed = username?.trim() ?? '';
  if (!trimmed) return '';
  if (isPublicSafeUsername(trimmed)) return trimmed;
  return 'athlete-unknown';
}
